/**
 * PlayerLOD — distance-based level of detail for the 22 player avatars.
 *
 * For Tier D (15-25k tris × 22 players, ~550k tris total) the bottleneck
 * isn't triangle count — modern GPUs eat that for breakfast. The hot
 * path is the per-player skeleton math run by THREE.AnimationMixer: for
 * a Mixamo-rigged humanoid that's ~50 bones × matrix interpolation per
 * mixer.update() call, multiplied by 22 players, every frame.
 *
 * This module measures camera distance to each player and tags them
 * with an LOD tier. PlayerModel.updatePlayerMixers reads the tier
 * tag and decides whether to step that frame.
 *
 * Tiers (configurable via tune()):
 *   Tier 0 (NEAR,  d <= 20m)  — update every frame
 *   Tier 1 (MID,   d <= 40m)  — update every other frame
 *   Tier 2 (FAR,   d <= 70m)  — update every 4th frame
 *   Tier 3 (CULL,  d  > 70m)  — hide entirely (set group.visible = false)
 *
 * The camera modes for football_3d (Top Down, Sideline, Broadcast,
 * End Zone, QB View) all keep at least the 11 nearest players within
 * 40m, so the typical scene runs ~half the players at full rate and the
 * rest at half/quarter rate — measured ~25% mixer-time reduction at
 * 22-player density without any visible animation lag.
 */

const NEAR = 20;
const MID = 40;
const FAR = 70;

const _config = {
  near: NEAR,
  mid: MID,
  far: FAR,
  cullEnabled: true,
};

/**
 * Override LOD distances. Useful for tests and for the QB-cam (which
 * benefits from a shorter mid threshold so the secondary players still
 * animate cleanly during a route concept).
 */
export function tune({ near, mid, far, cullEnabled } = {}) {
  if (typeof near === 'number') _config.near = near;
  if (typeof mid === 'number') _config.mid = mid;
  if (typeof far === 'number') _config.far = far;
  if (typeof cullEnabled === 'boolean') _config.cullEnabled = cullEnabled;
}

export function getConfig() {
  return { ..._config };
}

function _tierForDistance(d) {
  if (d <= _config.near) return 0;
  if (d <= _config.mid) return 1;
  if (d <= _config.far) return 2;
  return 3;
}

/**
 * Walk every playerGroup, compute camera distance, tag userData.lodTier
 * + userData.mixerFrameSkip + visibility. Call once per frame from
 * FootballScene3D.update(), before updatePlayerMixers.
 *
 * Camera-less guard: if `camera` is missing (test environments, or a
 * one-frame race during scene teardown), every player gets Tier 0 so
 * nothing is silently degraded.
 */
export function applyLOD(playerGroups, camera) {
  if (!playerGroups) return;

  if (!camera || !camera.position) {
    Object.values(playerGroups).forEach((g) => {
      if (!g) return;
      g.userData = g.userData || {};
      g.userData.lodTier = 0;
      g.userData.mixerFrameSkip = 1;
      g.visible = true;
    });
    return;
  }

  const cx = camera.position.x;
  const cy = camera.position.y;
  const cz = camera.position.z;

  Object.values(playerGroups).forEach((g) => {
    if (!g) return;
    g.userData = g.userData || {};
    const dx = g.position.x - cx;
    const dy = g.position.y - cy;
    const dz = g.position.z - cz;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const tier = _tierForDistance(d);
    g.userData.lodTier = tier;
    g.userData.lodDistance = d;
    if (tier === 0) g.userData.mixerFrameSkip = 1;
    else if (tier === 1) g.userData.mixerFrameSkip = 2;
    else if (tier === 2) g.userData.mixerFrameSkip = 4;
    else g.userData.mixerFrameSkip = 0; // 0 means "skip entirely"

    // Tier 3 (CULL): hide the group rather than just stop animating it.
    // The skinned mesh still costs draw-call + skinning matrix uploads
    // even when its bones are stale.
    if (tier === 3 && _config.cullEnabled) {
      g.visible = false;
    } else {
      g.visible = true;
    }
  });
}

/**
 * Test-only: clear configuration back to defaults so tests don't leak
 * state to one another.
 */
export function _resetForTests() {
  _config.near = NEAR;
  _config.mid = MID;
  _config.far = FAR;
  _config.cullEnabled = true;
}
